import React, { useMemo, useState } from "react";
import {
  LayoutDashboard,
  FileText,
  Save,
  Users,
  BookOpen,
  CalendarDays,
  LayoutTemplate,
  Layers,
  Package,
  Scissors,
  Settings,
  UserCog,
  Wrench,
  Moon,
  LogOut,
  X,
} from "lucide-react";

const menu = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { id: "quote", label: "Quotes", icon: FileText },
  { id: "saved", label: "Saved Quotes", icon: Save },
  { id: "crm", label: "CRM", icon: Users },
  { id: "customerLedger", label: "Customer Ledger", icon: BookOpen },
  { id: "todayTasks", label: "Today's Tasks", icon: CalendarDays },
  { id: "executionTemplates", label: "Execution Templates", icon: LayoutTemplate },
  { id: "fco", label: "Roll Optimizer", icon: Layers },
  { id: "products", label: "Products & Calculations", icon: Package },
  { id: "stitching", label: "Stitching & Tracks", icon: Scissors },
  { id: "teamAccess", label: "Team & Access", icon: UserCog },
  { id: "accountSecurity", label: "Account Security", icon: UserCog },
  { id: "maintenance", label: "Maintenance", icon: Wrench },
  { id: "settings", label: "Settings", icon: Settings },
];

const COLLAPSED_WIDTH = 84;
const EXPANDED_WIDTH = 260;
const MOBILE_WIDTH = 286;

const formatSidebarBadgeValue = (value) => {
  const count = Number(value || 0);
  if (!Number.isFinite(count) || count <= 0) return "";
  return count > 99 ? "99+" : String(Math.round(count));
};

export default function Sidebar({
  activeTab,
  onTabChange,
  isDark,
  onToggleDarkMode,
  onSignOut,
  isMobile = false,
  mobileOpen = false,
  onClose,
  brandName = "FabriQuote",
  visibleItemIds = null,
  itemBadges = {},
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [hoveredItemId, setHoveredItemId] = useState("");

  const items = useMemo(() => {
    if (!Array.isArray(visibleItemIds) || visibleItemIds.length === 0) {
      return menu;
    }

    return menu.filter((item) => visibleItemIds.includes(item.id));
  }, [visibleItemIds]);

  const expanded = isMobile || isExpanded;
  const shellWidth = isMobile ? MOBILE_WIDTH : COLLAPSED_WIDTH;
  const panelWidth = isMobile ? MOBILE_WIDTH : expanded ? EXPANDED_WIDTH : COLLAPSED_WIDTH;

  const isActive = (itemId) =>
    itemId === "dashboard"
      ? activeTab === "dashboard" || activeTab === "dashboardList"
      : activeTab === itemId;

  const shellBg = isDark ? "#0f172a" : "#f8fafc";
  const panelBg = isDark ? "rgba(15,23,42,0.96)" : "rgba(255,255,255,0.96)";
  const sectionBg = isDark ? "rgba(30,41,59,0.72)" : "rgba(248,250,252,0.92)";
  const textColor = isDark ? "#e2e8f0" : "#0f172a";
  const mutedColor = isDark ? "#94a3b8" : "#64748b";
  const navTextColor = isDark ? "#cbd5e1" : "#334155";
  const activeColor = isDark ? "#bfdbfe" : "#1d4ed8";
  const hoverBg = isDark ? "rgba(148,163,184,0.12)" : "#eef4ff";
  const borderColor = isDark ? "rgba(148,163,184,0.16)" : "rgba(226,232,240,0.95)";

  const labelStyle = {
    opacity: expanded ? 1 : 0,
    maxWidth: expanded ? 180 : 0,
    overflow: "hidden",
    whiteSpace: "nowrap",
    transition: "opacity 160ms ease, max-width 220ms ease, transform 220ms ease",
    transform: expanded ? "translateX(0)" : "translateX(-4px)",
    pointerEvents: expanded ? "auto" : "none",
  };

  const handleNavClick = (itemId) => {
    onTabChange(itemId);
    if (isMobile) onClose?.();
  };

  if (isMobile && !mobileOpen) {
    return null;
  }

  const sidebarPanel = (
    <aside
      onMouseEnter={() => !isMobile && setIsExpanded(true)}
      onMouseLeave={() => !isMobile && setIsExpanded(false)}
      onFocus={() => !isMobile && setIsExpanded(true)}
      onBlur={(event) => {
        if (!isMobile && !event.currentTarget.contains(event.relatedTarget)) {
          setIsExpanded(false);
        }
      }}
      style={{
        width: panelWidth,
        height: "100vh",
        position: isMobile ? "relative" : "absolute",
        inset: isMobile ? "auto" : "0 auto 0 0",
        top: 0,
        left: 0,
        padding: 10,
        display: "flex",
        flexDirection: "column",
        gap: 10,
        background: panelBg,
        backdropFilter: "blur(18px)",
        borderRight: `1px solid ${borderColor}`,
        boxShadow: expanded
          ? isDark
            ? "18px 0 48px rgba(2,6,23,0.46)"
            : "18px 0 48px rgba(15,23,42,0.14)"
          : isDark
          ? "0 0 0 1px rgba(15,23,42,0.12)"
          : "0 12px 36px rgba(15,23,42,0.04)",
        overflow: "hidden",
        transition: "width 220ms ease, box-shadow 220ms ease, background 220ms ease",
        zIndex: isMobile ? 52 : 30,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          minHeight: 62,
          padding: expanded ? "10px 12px" : "10px",
          borderRadius: 22,
          background: sectionBg,
          border: `1px solid ${borderColor}`,
          transition: "padding 220ms ease",
        }}
      >
        {isMobile ? (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close navigation"
            style={{
              width: 38,
              height: 38,
              minWidth: 38,
              borderRadius: 14,
              border: "none",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: isDark ? "rgba(148,163,184,0.14)" : "#e2e8f0",
              color: textColor,
              cursor: "pointer",
            }}
          >
            <X size={18} strokeWidth={2.2} />
          </button>
        ) : null}
        <div
          style={{
            width: 42,
            height: 42,
            minWidth: 42,
            borderRadius: 16,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "linear-gradient(135deg, #2563eb, #1d4ed8)",
            color: "#ffffff",
            fontWeight: 900,
            fontSize: 14,
            boxShadow: "0 16px 28px rgba(37,99,235,0.30)",
          }}
        >
          FQ
        </div>

        <div style={labelStyle}>
          <div
            style={{
              fontSize: 16,
              fontWeight: 800,
              letterSpacing: "-0.035em",
              color: textColor,
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {brandName}
          </div>
          <div
            style={{
              fontSize: 12,
              color: mutedColor,
              marginTop: 2,
            }}
          >
            Quotation workspace
          </div>
        </div>
      </div>

      <nav
        style={{
          flex: "1 1 auto",
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
          display: "grid",
          alignContent: "start",
          gap: 6,
          padding: expanded ? "8px" : "8px 6px",
          borderRadius: 22,
          background: sectionBg,
          border: `1px solid ${borderColor}`,
          scrollbarWidth: "thin",
        }}
      >
        {items.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.id);
          const hovered = hoveredItemId === item.id;
          const badgeValue = formatSidebarBadgeValue(itemBadges[item.id]);
          const badgeLabel = badgeValue
            ? `${item.label}: ${badgeValue} ${badgeValue === "1" ? "item" : "items"} need attention`
            : "";

          return (
            <button
              key={item.id}
              type="button"
              title={!expanded ? badgeLabel || item.label : undefined}
              onClick={() => handleNavClick(item.id)}
              onMouseEnter={() => setHoveredItemId(item.id)}
              onMouseLeave={() => setHoveredItemId("")}
              style={{
                position: "relative",
                width: "100%",
                minHeight: 44,
                display: "flex",
                alignItems: "center",
                gap: expanded ? 12 : 0,
                justifyContent: expanded ? "flex-start" : "center",
                padding: expanded ? "11px 12px" : "11px 0",
                borderRadius: 16,
                border: "none",
                cursor: "pointer",
                background: active
                  ? isDark
                    ? "rgba(37,99,235,0.24)"
                    : "#eaf2ff"
                  : hovered
                  ? hoverBg
                  : "transparent",
                color: active ? activeColor : navTextColor,
                fontWeight: active ? 800 : 650,
                boxShadow: active
                  ? "inset 0 0 0 1px rgba(37,99,235,0.18)"
                  : "none",
                transition:
                  "background-color 160ms ease, color 160ms ease, box-shadow 160ms ease, padding 220ms ease, gap 220ms ease",
              }}
            >
              <Icon
                size={19}
                strokeWidth={active ? 2.5 : 2.15}
                style={{ flex: "0 0 auto" }}
              />
              <span
                style={{
                  ...labelStyle,
                  fontSize: 14,
                  color: active ? activeColor : navTextColor,
                  textAlign: "left",
                }}
              >
                {item.label}
              </span>
              {badgeValue ? (
                <span
                  aria-label={badgeLabel}
                  title={badgeLabel}
                  style={{
                    minWidth: expanded ? 24 : 18,
                    height: expanded ? 22 : 18,
                    padding: expanded ? "0 8px" : "0 5px",
                    borderRadius: 999,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginLeft: expanded ? "auto" : 0,
                    position: expanded ? "static" : "absolute",
                    top: expanded ? "auto" : 7,
                    right: expanded ? "auto" : 9,
                    background: active ? "#1d4ed8" : "#dc2626",
                    color: "#ffffff",
                    fontSize: expanded ? 11 : 10,
                    fontWeight: 900,
                    lineHeight: 1,
                    boxShadow: "0 8px 18px rgba(220,38,38,0.22)",
                    pointerEvents: "none",
                  }}
                >
                  {badgeValue}
                </span>
              ) : null}
            </button>
          );
        })}
      </nav>

      <footer
        style={{
          flex: "0 0 auto",
          display: "grid",
          gap: 8,
          padding: expanded ? "8px" : "8px 6px",
          borderRadius: 22,
          background: sectionBg,
          border: `1px solid ${borderColor}`,
        }}
      >
        <button
          type="button"
          title={!expanded ? `Dark Mode ${isDark ? "On" : "Off"}` : undefined}
          onClick={onToggleDarkMode}
          onMouseEnter={() => setHoveredItemId("darkMode")}
          onMouseLeave={() => setHoveredItemId("")}
          style={{
            width: "100%",
            minHeight: 44,
            display: "flex",
            alignItems: "center",
            justifyContent: expanded ? "space-between" : "center",
            gap: expanded ? 10 : 0,
            padding: expanded ? "10px 10px" : "10px 0",
            borderRadius: 16,
            border: "none",
            cursor: "pointer",
            background:
              hoveredItemId === "darkMode"
                ? hoverBg
                : isDark
                ? "rgba(148,163,184,0.10)"
                : "#ffffff",
            color: textColor,
            boxShadow: isDark
              ? "inset 0 0 0 1px rgba(148,163,184,0.12)"
              : "inset 0 0 0 1px rgba(226,232,240,0.9)",
            transition: "background-color 160ms ease, padding 220ms ease",
          }}
        >
          <span
            style={{
              display: "flex",
              alignItems: "center",
              gap: expanded ? 12 : 0,
              minWidth: 0,
            }}
          >
            <Moon size={18} strokeWidth={2.15} style={{ flex: "0 0 auto" }} />
            <span
              style={{
                ...labelStyle,
                fontSize: 14,
                fontWeight: 750,
              }}
            >
              Dark Mode
            </span>
          </span>
          <span
            style={{
              opacity: expanded ? 1 : 0,
              maxWidth: expanded ? 48 : 0,
              overflow: "hidden",
              borderRadius: 999,
              padding: expanded ? "4px 8px" : "4px 0",
              background: isDark ? "rgba(37,99,235,0.22)" : "#e2e8f0",
              color: isDark ? "#bfdbfe" : "#475569",
              fontSize: 11,
              fontWeight: 900,
              transition: "opacity 160ms ease, max-width 220ms ease, padding 220ms ease",
            }}
          >
            {isDark ? "ON" : "OFF"}
          </span>
        </button>

        <button
          type="button"
          title={!expanded ? "Sign Out" : undefined}
          onClick={onSignOut}
          onMouseEnter={() => setHoveredItemId("signOut")}
          onMouseLeave={() => setHoveredItemId("")}
          style={{
            width: "100%",
            minHeight: 44,
            display: "flex",
            alignItems: "center",
            gap: expanded ? 12 : 0,
            justifyContent: expanded ? "flex-start" : "center",
            padding: expanded ? "10px 12px" : "10px 0",
            borderRadius: 16,
            border: "none",
            cursor: "pointer",
            background:
              hoveredItemId === "signOut"
                ? isDark
                  ? "rgba(127,29,29,0.40)"
                  : "#fee2e2"
                : isDark
                ? "rgba(127,29,29,0.28)"
                : "#fff1f2",
            color: "#dc2626",
            fontWeight: 850,
            boxShadow: isDark
              ? "inset 0 0 0 1px rgba(248,113,113,0.18)"
              : "inset 0 0 0 1px rgba(254,202,202,0.9)",
            transition: "background-color 160ms ease, padding 220ms ease, gap 220ms ease",
          }}
        >
          <LogOut size={18} strokeWidth={2.2} style={{ flex: "0 0 auto" }} />
          <span
            style={{
              ...labelStyle,
              fontSize: 14,
            }}
          >
            Sign Out
          </span>
        </button>
      </footer>
    </aside>
  );

  if (isMobile) {
    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 50,
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <button
          type="button"
          aria-label="Close navigation overlay"
          onClick={onClose}
          style={{
            flex: 1,
            border: "none",
            background: "rgba(15,23,42,0.42)",
            cursor: "pointer",
          }}
        />
        {sidebarPanel}
      </div>
    );
  }

  return (
    <div
      style={{
        width: shellWidth,
        minWidth: shellWidth,
        height: "100vh",
        position: "sticky",
        top: 0,
        background: shellBg,
        zIndex: 28,
        overflow: "visible",
      }}
    >
      {sidebarPanel}
    </div>
  );
}
